/**
 * Staff Evaluation Tab Component (Owner Only)
 * Handles annual staff performance evaluations
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { ClipboardList, Save, Trash2, History, Edit, FileText, Printer, Plus, Mail } from "lucide-react";
import { format } from "date-fns";
import { exportStaffEvaluationPDF } from "@/utils/pdfExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL_EVALUATION = {
  staffId: "",
  staffName: "",
  staffEmail: "",
  evaluationPeriodStart: "",
  evaluationPeriodEnd: "",
  jobKnowledge: "",
  qualityOfWork: "",
  reliability: "",
  communication: "",
  teamwork: "",
  initiative: "",
  professionalDevelopment: "",
  childInteraction: "",
  parentInteraction: "",
  safetyCompliance: "",
  strengths: "",
  areasForImprovement: "",
  goalsFromLastYear: "",
  goalsForNextYear: "",
  trainingCompleted: "",
  supervisorComments: "",
  staffComments: "",
  supervisorName: "",
  supervisorSignatureDate: "",
  staffAcknowledged: false,
  staffAcknowledgeDate: ""
};

const RATING_OPTIONS = [
  { value: "exceptional", label: "Exceptional" },
  { value: "exceeds", label: "Exceeds Expectations" },
  { value: "meets", label: "Meets Expectations" },
  { value: "needs_improvement", label: "Needs Improvement" },
  { value: "unsatisfactory", label: "Unsatisfactory" }
];

export const StaffEvaluationTab = ({ staffRecords, staff, isOwner, currentUserEmail, centerName = '' }) => {
  const [evaluation, setEvaluation] = useState(INITIAL_EVALUATION);
  const [savedEvaluations, setSavedEvaluations] = useState([]);
  const [savingEvaluation, setSavingEvaluation] = useState(false);
  const [editingEvaluationId, setEditingEvaluationId] = useState(null);
  const [autoDirectorName, setAutoDirectorName] = useState(true);
  const [showEvaluationHistory, setShowEvaluationHistory] = useState(false);

  const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";
  const isPrimaryOwner = currentUserEmail?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase();

  // Use staff records for evaluations (IDs and names match directly)
  const evaluatableStaff = (staffRecords && staffRecords.length > 0) ? staffRecords : (staff || []);

  useEffect(() => {
    if (isPrimaryOwner) {
      fetchEvaluations();
      // Auto-populate director name from owner's display name
      if (autoDirectorName && !evaluation.supervisorName) {
        const ownerName = staff?.find(s => s.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase())?.name || centerName || '';
        if (ownerName) updateField('supervisorName', ownerName);
      }
    }
  }, [isPrimaryOwner]);

  const fetchEvaluations = async () => {    try {
      const res = await axios.get(`${API}/staff-evaluations`);
      setSavedEvaluations(res.data?.evaluations || []);
    } catch (error) {
      console.error("Error fetching evaluations:", error);
    }
  };

  const updateField = (field, value) => {
    setEvaluation({ ...evaluation, [field]: value });
  };

  const handleSaveEvaluation = async () => {
    if (!evaluation.staffId || !evaluation.staffName) {
      toast.error("Please select a staff member");
      return;
    }
    if (!evaluation.evaluationPeriodEnd) {
      toast.error("Please enter the evaluation Period End date — this sets the expiration in the staff record");
      return;
    }

    setSavingEvaluation(true);
    try {
      if (editingEvaluationId) {
        await axios.put(`${API}/staff-evaluations/${editingEvaluationId}`, evaluation);
        toast.success("Evaluation updated");
      } else {
        const res = await axios.post(`${API}/staff-evaluations`, evaluation);
        const savedId = res.data?.evaluation?.id;
        if (savedId) setEditingEvaluationId(savedId);
        toast.success("Evaluation saved");
      }

      // Auto-feed handled by backend — no frontend PUT needed
      
      await fetchEvaluations();
    } catch (error) {
      console.error("Error saving evaluation:", error);
      toast.error("Failed to save evaluation");
    } finally {
      setSavingEvaluation(false);
    }
  };

  const handleEditEvaluation = (eval_) => {
    setEvaluation(eval_);
    setEditingEvaluationId(eval_.id);
    setShowEvaluationHistory(false);
    toast.info("Loaded evaluation for editing");
  };

  const handleDeleteEvaluation = async (evalId) => {
    if (!window.confirm("Are you sure you want to delete this evaluation?")) return;
    
    try {
      await axios.delete(`${API}/staff-evaluations/${evalId}`);
      toast.success("Evaluation deleted");
      await fetchEvaluations();
    } catch (error) {
      console.error("Error deleting evaluation:", error);
      toast.error("Failed to delete evaluation");
    }
  };

  const handleClearForm = () => {
    setEvaluation(INITIAL_EVALUATION);
    setEditingEvaluationId(null);
  };

  const handleExportPDF = async () => {
    try {
      const staffMember = evaluatableStaff?.find(s => s.id === evaluation.staffId);
      await exportStaffEvaluationPDF(evaluation, staffMember, centerName);
      toast.success("Evaluation PDF downloaded");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleEmailEvaluation = async () => {
    if (!editingEvaluationId) {
      toast.error("Please save the evaluation first before emailing");
      return;
    }
    const staffMember = evaluatableStaff?.find(s => s.id === evaluation.staffId);
    const email = staffMember?.email || evaluation.staffEmail || "";
    if (!email) {
      toast.error("No email address found for this staff member");
      return;
    }
    try {
      await axios.post(`${API}/staff-evaluations/${editingEvaluationId}/email`, { recipientEmail: email });
      toast.success(`Evaluation emailed to ${email}`);
    } catch (error) {
      console.error("Email error:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleEmailFromHistory = async (eval_) => {
    const staffMember = evaluatableStaff?.find(s => s.id === eval_.staffId);
    const email = staffMember?.email || eval_.staffEmail || "";
    if (!email) {
      toast.error("No email address found for this staff member");
      return;
    }
    try {
      await axios.post(`${API}/staff-evaluations/${eval_.id}/email`, { recipientEmail: email });
      toast.success(`Evaluation re-sent to ${email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleExportPDFFromHistory = async (eval_) => {
    try {
      const staffMember = evaluatableStaff?.find(s => s.id === eval_.staffId);
      await exportStaffEvaluationPDF(eval_, staffMember, centerName);
      toast.success("Evaluation PDF downloaded");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handlePrint = () => window.print();

  // Access control check
  if (!isPrimaryOwner) {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-100">
        <CardContent className="py-12 text-center">
          <ClipboardList className="w-16 h-16 text-blue-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-blue-800 mb-2">Access Restricted</h3>
          <p className="text-gray-600">Staff evaluations are only accessible to the primary owner account.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-100">
      <CardHeader className="print:hidden">
        <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
          <ClipboardList className="w-7 h-7" />
          Staff Evaluation
          {editingEvaluationId && (
            <span className="text-sm font-normal bg-blue-50 text-blue-800 px-2 py-1 rounded ml-2">
              Editing Evaluation
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Staff Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:hidden">
          <div>
            <Label className="text-blue-800 font-semibold">Staff Member *</Label>
            <Select 
              value={evaluation.staffId} 
              onValueChange={(value) => {
                const staffMember = evaluatableStaff?.find(s => s.id === value);
                // Update all fields at once to avoid state batching issues
                setEvaluation(prev => ({
                  ...prev,
                  staffId: value,
                  staffName: staffMember?.name || '',
                  staffEmail: staffMember?.email || ''
                }));
              }}
            >
              <SelectTrigger className="border-blue-100 mt-1">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {evaluatableStaff?.map((staffMember) => (
                  <SelectItem key={staffMember.id} value={staffMember.id}>{staffMember.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Period Start</Label>
            <Input
              type="date"
              value={evaluation.evaluationPeriodStart}
              onChange={(e) => updateField('evaluationPeriodStart', e.target.value)}
              className="border-blue-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Period End <span className="text-red-500">*</span> <span className="text-xs font-normal text-slate-500">(sets expiration in staff record)</span></Label>
            <Input
              type="date"
              value={evaluation.evaluationPeriodEnd}
              onChange={(e) => updateField('evaluationPeriodEnd', e.target.value)}
              className={`mt-1 ${!evaluation.evaluationPeriodEnd ? 'border-red-300 bg-red-50' : 'border-blue-100'}`}
            />
          </div>
        </div>

        {/* Performance Ratings */}
        <div className="border-2 border-blue-50 rounded-lg p-6 bg-white">
          <h3 className="font-semibold text-lg text-blue-800 mb-4">Performance Ratings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { field: 'jobKnowledge', label: 'Job Knowledge' },
              { field: 'qualityOfWork', label: 'Quality of Work' },
              { field: 'reliability', label: 'Reliability' },
              { field: 'communication', label: 'Communication' },
              { field: 'teamwork', label: 'Teamwork' },
              { field: 'initiative', label: 'Initiative' },
              { field: 'professionalDevelopment', label: 'Professional Development' },
              { field: 'childInteraction', label: 'Child Interaction' },
              { field: 'parentInteraction', label: 'Parent Interaction' },
              { field: 'safetyCompliance', label: 'Safety Compliance' }
            ].map(({ field, label }) => (
              <div key={field}>
                <Label className="text-blue-700 text-sm">{label}</Label>
                <Select value={evaluation[field]} onValueChange={(v) => updateField(field, v)}>
                  <SelectTrigger className="border-blue-100 mt-1">
                    <SelectValue placeholder="Select rating" />
                  </SelectTrigger>
                  <SelectContent>
                    {RATING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>

        {/* Comments Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-blue-800 font-semibold">Strengths</Label>
            <Textarea
              value={evaluation.strengths}
              onChange={(e) => updateField('strengths', e.target.value)}
              placeholder="List key strengths..."
              className="border-blue-100 mt-1 min-h-[100px]"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Areas for Improvement</Label>
            <Textarea
              value={evaluation.areasForImprovement}
              onChange={(e) => updateField('areasForImprovement', e.target.value)}
              placeholder="List areas needing improvement..."
              className="border-blue-100 mt-1 min-h-[100px]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-blue-800 font-semibold">Goals from Last Year</Label>
            <Textarea
              value={evaluation.goalsFromLastYear}
              onChange={(e) => updateField('goalsFromLastYear', e.target.value)}
              placeholder="Review of last year's goals..."
              className="border-blue-100 mt-1 min-h-[80px]"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Goals for Next Year</Label>
            <Textarea
              value={evaluation.goalsForNextYear}
              onChange={(e) => updateField('goalsForNextYear', e.target.value)}
              placeholder="Set goals for coming year..."
              className="border-blue-100 mt-1 min-h-[80px]"
            />
          </div>
        </div>

        <div>
          <Label className="text-blue-800 font-semibold">Training Completed</Label>
          <Textarea
            value={evaluation.trainingCompleted}
            onChange={(e) => updateField('trainingCompleted', e.target.value)}
            placeholder="List training and certifications completed..."
            className="border-blue-100 mt-1"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-blue-800 font-semibold">Director Comments</Label>
            <Textarea
              value={evaluation.supervisorComments}
              onChange={(e) => updateField('supervisorComments', e.target.value)}
              placeholder="Additional director comments..."
              className="border-blue-100 mt-1 min-h-[80px]"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Staff Comments</Label>
            <Textarea
              value={evaluation.staffComments}
              onChange={(e) => updateField('staffComments', e.target.value)}
              placeholder="Staff member's comments..."
              className="border-blue-100 mt-1 min-h-[80px]"
            />
          </div>
        </div>

        {/* Signature Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t-2 border-blue-50 pt-4">
          <div>
            <Label className="text-blue-800 font-semibold flex items-center justify-between">
              <span>Director Name</span>
              <span className="flex items-center gap-2 text-xs font-normal text-slate-500">
                Auto-populate
                <button type="button" onClick={() => {
                  const next = !autoDirectorName;
                  setAutoDirectorName(next);
                  if (next) {
                    const ownerName = staff?.find(s => s.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase())?.name || centerName || '';
                    if (ownerName) updateField('supervisorName', ownerName);
                  }
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoDirectorName ? 'bg-blue-500' : 'bg-slate-300'}`}>
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${autoDirectorName ? 'translate-x-4' : 'translate-x-1'}`} />
                </button>
              </span>
            </Label>
            <Input
              value={evaluation.supervisorName}
              onChange={(e) => updateField('supervisorName', e.target.value)}
              placeholder="Director name"
              className="border-blue-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-blue-800 font-semibold">Director Signature Date</Label>
            <Input
              type="date"
              value={evaluation.supervisorSignatureDate}
              onChange={(e) => updateField('supervisorSignatureDate', e.target.value)}
              className="border-blue-100 mt-1"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between flex-wrap gap-2 print:hidden">
          <div className="flex gap-2">
            <Button onClick={handleClearForm} variant="outline" className="border-blue-200 text-blue-700 hover:bg-slate-50">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Form
            </Button>
            <Button onClick={() => setShowEvaluationHistory(!showEvaluationHistory)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
              <History className="w-4 h-4 mr-2" />
              History ({savedEvaluations.length})
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveEvaluation}
              disabled={!evaluation.staffId || savingEvaluation}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {savingEvaluation ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingEvaluationId ? "Update" : "Save"} Evaluation
                </>
              )}
            </Button>
            <Button onClick={handleExportPDF} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" disabled={!evaluation.staffId} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handleEmailEvaluation} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" disabled={!editingEvaluationId} data-testid="email-eval-btn">
              <Mail className="w-4 h-4 mr-2" />
              Email to Staff
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* History Panel */}
        {showEvaluationHistory && (
          <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
            <h3 className="text-lg font-semibold text-purple-800 mb-4">Saved Evaluations</h3>
            {savedEvaluations.length === 0 ? (
              <p className="text-gray-500 italic">No saved evaluations yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {savedEvaluations.map((eval_) => (
                  <div key={eval_.id} className="bg-white p-3 rounded-lg border border-purple-200 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-purple-800">{eval_.staffName}</p>
                      <p className="text-sm text-gray-600">
                        Period: {eval_.evaluationPeriodStart} to {eval_.evaluationPeriodEnd}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {eval_.createdAt ? format(new Date(eval_.createdAt), 'MMM d, yyyy') : 'Unknown'}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      <Button
                        onClick={() => handleExportPDFFromHistory(eval_)}
                        size="sm" variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                        title="Download PDF"
                        data-testid={`view-eval-pdf-${eval_.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleEmailFromHistory(eval_)}
                        size="sm" variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50"
                        title="Re-send to staff"
                        data-testid={`email-eval-history-${eval_.id}`}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleEditEvaluation(eval_)} size="sm" variant="outline" className="border-purple-300 text-purple-700" title="Edit evaluation">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => handleDeleteEvaluation(eval_.id)} size="sm" variant="outline" className="border-red-300 text-red-700" title="Delete evaluation">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

    </Card>
  );
};

export default StaffEvaluationTab;
